/* eslint-disable no-undef */
import Vue from "vue";
import App from "./App.vue";

import * as Sentry from "@sentry/browser";
import { tagAxiosFailure } from "./utils/sentryAxiosContext.mjs";

import _ from "lodash";
import store from "./store";

import "@/assets/global.css";
import "@/assets/mecom/tokens.css";
import "@/assets/mecom/mecom-ui.css";
import "@/assets/mecom/mecom-vuetify.css";
import { ApolloClient, from, HttpLink, InMemoryCache } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { captureException } from "@sentry/vue";
import { createApolloProvider } from "@vue/apollo-option";
import { logErrorMessages } from "@vue/apollo-util";
import { persistCache } from "apollo3-cache-persist";
import { BoundedApolloStorage } from "./utils/apolloStorage.mjs";
import axios from "axios";
import localforage from "localforage";
import "material-design-icons-iconfont/dist/material-design-icons.css";
import Vlf from "vlf";
import VueAxios from "vue-axios";
import VueMoment from "vue-moment";
import VueWorker from "vue-worker";
import Api from "./plugins/api";
import vuetify from "./plugins/vuetify";
import router from "./router";
import socket from "./socket";
import { ensureCapabilities } from "./utils/accessControl.mjs";

const moment = require("moment");
const momentDurationFormatSetup = require("moment-duration-format");
momentDurationFormatSetup(moment);
require("moment/locale/it");

function isPdfAnnotationSvgDimensionError(error, event) {
    if (error?.name !== "Error" || error.message !== "Invalid SVG dimensions") {
        return false;
    }

    const errorStack = error.stack || "";
    if (errorStack.includes("DOMSVGFactory") || errorStack.includes("SquareAnnotationElement")) {
        return true;
    }

    const stack = event?.exception?.values?.[0]?.stacktrace?.frames || [];
    return stack.some((frame) => {
        const filename = frame.filename || "";
        const functionName = frame.function || "";
        return (
            filename.includes("vue-pdf-app") ||
            functionName.includes("DOMSVGFactory") ||
            functionName.includes("SquareAnnotationElement")
        );
    });
}

function isOseonAxiosNetworkErrorNoise(error, event) {
    if (error?.name !== "Error" || error.message !== "Aa") {
        return false;
    }

    const oseonEndpoints = [
        "/oseon/v2/jobs-in-progress/",
        "/laser/sbancalatura/conteggio/",
        "/laser/sbancalatura/conteggio_da_ubicare/",
        "/oseon/v2/jobs-to-start/",
    ];
    const recentBreadcrumbs = (event?.breadcrumbs || []).slice(-12);

    return recentBreadcrumbs.some((breadcrumb) => {
        const message = breadcrumb?.message || "";
        const data = breadcrumb?.data || {};
        const values = [message, data.url, data.to, data.from, data.arguments]
            .flat()
            .filter(Boolean)
            .map((value) => String(value));

        return values.some((value) => value.includes("Network Error")) && values.some((value) => {
            return oseonEndpoints.some((endpoint) => value.includes(endpoint));
        });
    });
}

function isPdfRelayoutUndefinedRejection(error, event) {
    if (error !== undefined && error !== null) {
        return false;
    }

    const exception = event?.exception?.values?.[0];
    if (!exception || exception.type !== "UnhandledRejection") {
        return false;
    }

    if (!String(exception.value || "").includes("promise rejection captured with value: undefined")) {
        return false;
    }

    return (event?.breadcrumbs || []).slice(-5).some((breadcrumb) => {
        return breadcrumb?.category === "console" && String(breadcrumb.message || "").includes("Relayout fail");
    });
}

function isVuePdfCleanupRace(error, event) {
    if (!(error instanceof Error) || !/^startCleanup: Page \d+ is currently rendering\.$/.test(error.message)) {
        return false;
    }

    const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
    return frames.some((frame) => String(frame.filename || "").includes("vue-pdf-app"));
}

function isVuePdfReaderTeardownRace(error, event) {
    if (
        !(error instanceof Error) ||
        error.name !== "TypeError" ||
        !/(?:^|, )(?:\w+\._reader is null|can't access property "read", \w+\._reader is null)$/.test(error.message)
    ) {
        return false;
    }

    const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
    return frames.some((frame) => String(frame.filename || "").includes("vue-pdf-app"));
}

if (process.env.VUE_APP_SENTRY_ENV === "develop") {
    window.__VUE_DEVTOOLS_HOST__ = "localhost";
    window.__VUE_DEVTOOLS_PORT__ = 8098;
}

Sentry.init({
    Vue,
    enabled: process.env.VUE_APP_DISABLE_SENTRY !== "true",
    environment: process.env.VUE_APP_SENTRY_ENV || "production",
    dsn: "https://f625feadb8497ba37cc6b34b160f0742@sentry.meskatech.com/4",
    autoSessionTracking: true,
    release: "work-frontend@" + process.env.VUE_APP_PACKAGE_VERSION,
    integrations: [
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
        }),
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    tracePropagationTargets: ["work.mecomsrl.com", /^https:\/\/work.mecomsrl\.com/],
    beforeSend(event, hint) {
        // Solo produzione: dev, test e sviluppo locale non inviano eventi.
        if (event.environment && event.environment !== "production") {
            return null;
        }
        const error = hint.originalException;
        if (error?.message?.match(/database unavailable/i)) {
            event.fingerprint = ["database-unavailable"];
        }
        /*
         * Filtro errori incurabili
         * */
        if (error && error.name === "DataCloneError") {
            return null;
        }
        if (
            error &&
            error.name === "AxiosError" &&
            typeof error.message === "string" &&
            /^timeout of \d+ms exceeded$/.test(error.message)
        ) {
            return null;
        }
        if (error && error.name === "AxiosError" && error.message === "Request aborted") {
            return null;
        }
        if (error && error.name === "AxiosError" && error.message === "Network Error") {
            return null;
        }
        if (error && error.name === "Error" && error.message === "Worker was terminated") {
            return null;
        }
        if (
            error &&
            error.name === "Error" &&
            error.message === "Resize must be passed a displayed plot div element."
        ) {
            return null;
        }
        if (
            error &&
            error.name === "TypeError" &&
            typeof error.message === "string" &&
            /(?:^|, )this\.messageHandler is null$/.test(error.message)
        ) {
            return null;
        }
        if (
            error &&
            error.name === "TypeError" &&
            typeof error.message === "string" &&
            /null is not an object \(evaluating 'this\.messageHandler\.sendWithPromise'\)/.test(error.message)
        ) {
            // Xe la stessa race di teardown del viewer PDF, ma Safari la formula in modo diverso.
            return null;
        }
        if (isPdfAnnotationSvgDimensionError(error, event)) {
            return null;
        }
        if (isOseonAxiosNetworkErrorNoise(error, event)) {
            return null;
        }
        if (isPdfRelayoutUndefinedRejection(error, event)) {
            return null;
        }
        if (isVuePdfReaderTeardownRace(error, event)) {
            // Firefox: reader dello stream PDF azzerato dal teardown del viewer (WORK-FRONTEND-6N).
            return null;
        }
        if (isVuePdfCleanupRace(error, event)) {
            // Xe una race nota del viewer: la UI continua a funzionare, no serve alertar Sentry.
            return null;
        }
        // Errori HTTP axios: metodo, URL normalizzato e status come tag/fingerprint.
        return tagAxiosFailure(event, error);
    },
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.2,
    logErrors: true,
});
Sentry.addTracingExtensions();

Vue.config.productionTip = false;
Vue.config.performance = true;
Vue.prototype.$socket = socket;

// Variabili Globali da ENV
const workProtocol = process.env.VUE_APP_WORK_PROTOCOL || window.location.protocol;
Vue.prototype.$workUrl = workProtocol + "//" + process.env.VUE_APP_WORK_URL;
Vue.prototype.$workWs = process.env.VUE_APP_WORK_WS;
Vue.prototype.$sentryEnv = process.env.VUE_APP_SENTRY_ENV;
Vue.prototype.$AppVersion = process.env.VUE_APP_PACKAGE_VERSION;
Vue.prototype.$ThumborKey = process.env.VUE_APP_THUMBOR_KEY;
Vue.prototype.$ThumborUrl = window.location.protocol + "//" + process.env.VUE_APP_THUMBOR_URL;

Vue.prototype.$pouchUrl =
    window.location.protocol +
    "//" +
    process.env.VUE_APP_POUCHDB_USER +
    ":" +
    process.env.VUE_APP_POUCHDB_PASSWORD +
    "@" +
    process.env.VUE_APP_POUCHDB_URL +
    "/";

// il caricamento della versione viene fatto su vue.config.js
// il bump versione non è più automatico via git hook: prima del push su master eseguire un bump patch manuale

Vue.use(VueMoment, { moment });
Vue.use(VueWorker);
Vue.use(Vlf, localforage);
Vue.use(Api, {
    url: Vue.prototype.$workUrl + "/api/",
    store: store,
    version: Vue.prototype.$AppVersion,
});
Vue.use(VueAxios, axios);
Vue.axios.defaults.baseURL = workProtocol + "//" + process.env.VUE_APP_WORK_URL;

Vue.filter("round", function (value, precision) {
    if (precision === undefined) {
        precision = 2;
    }
    if (!value) return "";
    if (value === 0) return 0;
    return _.round(value, precision);
});

Vue.filter("toEur", function (value, precision = 2, hideZero = false) {
    if (typeof value !== "number") {
        value = _.round(Number(value), precision);
    } else {
        value = _.round(value, precision);
    }
    if (hideZero && value === 0) {
        return "";
    }
    const formatter = new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: precision,
        minimumSignificantDigits: 1,
    });
    return formatter.format(value);
});

Vue.filter("noZero", function (value, precision = 2) {
    if (typeof value !== "number") {
        value = _.round(Number(value), precision);
    } else {
        value = _.round(value, precision);
    }
    if (value === 0) {
        return "";
    }
    const formatter = new Intl.NumberFormat("it-IT", {
        style: "decimal",
    });
    return formatter.format(value);
});

Vue.filter("toQty", function (value, precision = 2) {
    if (typeof value !== "number") {
        value = _.round(Number(value), precision);
    } else {
        value = _.round(value, precision);
    }
    const formatter = new Intl.NumberFormat("it-IT", {
        style: "decimal",
    });
    return formatter.format(value);
});
Vue.filter("toPerc", function (value) {
    if (typeof value !== "number") {
        value = Number(value);
    }
    if (value <= 0) {
        return "";
    }
    const formatter = new Intl.NumberFormat("it-IT", {
        style: "percent",
    });
    return formatter.format(value / 100);
});
Vue.filter("toPerc2", function (value) {
    if (typeof value !== "number") {
        value = Number(value);
    }
    if (value === 0) {
        return "";
    }
    const formatter = new Intl.NumberFormat("it-IT", {
        style: "percent",
    });
    return formatter.format(value / 100);
});
Vue.filter("join", function (array, separator) {
    return _.join(array, separator.replace(/\n/g, "<br />"));
});

Vue.filter("cut", function (text, stop) {
    return text.slice(0, stop);
});

Vue.filter("truncate", function (text, stop, clamp) {
    return text.slice(0, stop) + (stop < text.length ? clamp || "..." : "");
});

Vue.filter("boringduration", function (array) {
    let duration = moment.duration(array[0], array[1]).format("h:mm:ss", { trim: false });
    // se il primo carattere non è un numero, lo rimuovo
    if (isNaN(duration.charAt(0))) {
        duration = duration.slice(1);
    }
    return duration;
});

Vue.filter("highlight", function (text, search) {
    if (!search) {
        return text;
    }
    return text.replace(new RegExp(search, "gi"), '<span class="highlightedText">$&</span>');
});

export const bus = new Vue();
Vue.prototype.$bus = bus;

// Apollo
// Avvia il controllo già richiesto da router/App durante il ripristino della cache.
// ensureCapabilities accorpa la richiesta; il router continua ad attenderne l'esito.
if (store.getters.isLoggedIn) {
    ensureCapabilities(store, window.work_api).catch(() => { /* Il router gestisce l'errore e l'eventuale nuovo tentativo. */ });
}
const cache = new InMemoryCache();
persistCache({
    cache,
    storage: new BoundedApolloStorage(localforage),
    serialize: false,
})
    .catch((error) => {
        // Se IndexedDB fa el mona, partimo stesso senza cache persistente.
        console.warn("Apollo persistent cache disabled", error);
    })
    .then(() => {
    const authLink = setContext((_, { headers }) => {
        const token = store.getters.token;

        return {
            headers: {
                ...headers,
                Accept: "application/json",
                ...(token ? { Authorization: "Token " + token } : {}),
            },
        };
    });

    const additiveLink = from([
        new RetryLink(),
        // El token pol cambiar dopo login: leggilo live, no sta congelarlo al boot.
        authLink,
        new HttpLink({
            uri: `${workProtocol}//${process.env.VUE_APP_WORK_URL}/graphql/`,
        }),
    ]);

    // Handle errors
    const errorLink = onError((error) => {
        const { graphQLErrors, networkError } = error;
        const isLoginParseNoise =
            router.currentRoute?.path === "/login" && networkError?.name === "ServerParseError";
        // El duplicato Sage xe già gestìo dal form, no sta far rumor in Sentry.
        const isDuplicateConstraint = (graphQLError) =>
            /ORA-00001:\s*unique constraint/i.test(graphQLError?.message || "");
        // El path relativo xe na validassion già mostrà al cliente, no un crash del frontend.
        const isRelativeDrawingPathValidation = (graphQLError) =>
            /invalid relative drawing path/i.test(graphQLError?.message || "");

        if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
            graphQLErrors.forEach((graphQLError) => {
                if (isDuplicateConstraint(graphQLError) || isRelativeDrawingPathValidation(graphQLError)) return;

                captureException(
                    graphQLError instanceof Error
                        ? graphQLError
                        : new Error(graphQLError?.message || "Apollo GraphQL error")
                );
            });
        } else if (networkError && !isLoginParseNoise) {
            captureException(
                networkError instanceof Error
                    ? networkError
                    : new Error(networkError?.message || "Apollo network error")
            );
        } else {
            captureException(new Error(error?.message || "Apollo operation error"));
        }

        if (process.env.NODE_ENV !== "production") {
            logErrorMessages(error);
        }
    });

    const apolloClient = new ApolloClient({
        cache,
        link: errorLink.concat(additiveLink),
    });

    const apolloProvider = createApolloProvider({
        defaultClient: apolloClient,
    });

    Vue.config.globalProperties = Vue.config.globalProperties || {};
    Vue.prototype.$apolloProvider = apolloProvider;
    apolloProvider.install(Vue);

    window.vm = new Vue({
        store,
        vuetify,
        router,
        render: (h) => h(App),
    }).$mount("#app");
});

window.testBarcode = function (odp) {
    /*
     TEST BARCODE
     lanciare con:
     testBarcode('ODP1007384')
    */
    for (let x in odp) {
        window.dispatchEvent(
            new KeyboardEvent("keydown", {
                key: odp[x],
            })
        );
    }
    window.dispatchEvent(
        new KeyboardEvent("keydown", {
            key: "Enter",
        })
    );
};
